/* eslint-disable react-hooks/exhaustive-deps */
import {
  Box,
  Button,
  Container,
  Fade,
  List,
  ListItem,
  Stack,
  Step,
  StepButton,
  Stepper,
  Typography,
} from "@mui/material";
import { skipToken } from "@reduxjs/toolkit/query";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { useGetIntakeQuestionOptionsQuery } from "src/api/services/intakeQuestionOptionsApi";
import {
  useGetIntakeQuestionQuery,
  useGetIntakeQuestionsQuery,
} from "src/api/services/intakeQuestionsApi";
import { QuestionType } from "src/api/types";
import QuestionBuilder from "src/components/QuestionBuilder";
import QuestionOptionsBuilder from "src/components/QuestionOptionsBuilder";
import { MatchParam } from "src/constants/matches";
import { useQueryParams } from "src/hooks/useQueryParams";

const FADE_DURATION = 300;
const QUESTIONS_WITH_CHOICES: QuestionType[] = [
  "single_choice",
  "multiple_choice",
  "text",
];

const IntakeBuilder = () => {
  const { intakeId } = useParams<MatchParam<"INTAKE_ID">>();
  const [searchParams] = useSearchParams();
  const { setParams, deleteParams } = useQueryParams();
  const { data: questions = [] } = useGetIntakeQuestionsQuery(
    intakeId != null ? Number(intakeId) : skipToken,
    { refetchOnMountOrArgChange: true },
  );
  const latestQuestion = useMemo(
    () => questions[questions.length - 1],
    [questions],
  );
  const [activeStep, setActiveStep] = useState<number>(0);
  const { data: question, isLoading: questionLoading } =
    useGetIntakeQuestionQuery(
      intakeId != null && questions.length > 0
        ? {
            intakeId: Number(intakeId),
            questionPosition: activeStep,
          }
        : skipToken,
    );
  const { data: options = [] } = useGetIntakeQuestionOptionsQuery(
    intakeId != null && question != null
      ? { intakeId: Number(intakeId), questionId: question.id }
      : skipToken,
    { refetchOnMountOrArgChange: true },
  );
  const showOptions = searchParams.get("form") === "options";

  const handleBackToBuilder = useCallback(() => {
    deleteParams(["form"]);
  }, [deleteParams]);

  const handleQuestionPosition = useCallback(
    (step: number) => {
      setActiveStep(step);
      setParams({ position: step.toString(), form: "options" });
    },
    [setParams],
  );

  useEffect(() => {
    if (latestQuestion != null) {
      setActiveStep(latestQuestion.question_position ?? 0);
      setParams({
        position: (latestQuestion.question_position ?? 0).toString(),
      });
    }
  }, [latestQuestion?.id]);

  useEffect(() => {
    if (
      !questionLoading &&
      question != null &&
      QUESTIONS_WITH_CHOICES.includes(question.question_type)
    ) {
      setParams({ form: "options" });
    }
  }, [question?.id, questionLoading]);

  return (
    <Container maxWidth="md">
      <Stack gap={3}>
        <Box
          sx={{
            overflowX: "auto",
            overflowY: "hidden",
            width: "100%",
            // Hide scrollbar visually but keep functionality
            msOverflowStyle: "none",
            scrollbarWidth: "thin",
            p: 1,
          }}
        >
          <Stepper
            nonLinear
            activeStep={activeStep}
            sx={{
              "& .MuiStepIcon-root": { width: 32, height: 32 },
            }}
          >
            {questions.map((q) => (
              <Step key={q.question_position}>
                <StepButton
                  icon={String(q.question_position)}
                  onClick={() =>
                    handleQuestionPosition(q.question_position ?? 0)
                  }
                />
              </Step>
            ))}
          </Stepper>
        </Box>
        <Fade in={questions.length === 0} timeout={FADE_DURATION} unmountOnExit>
          <div>
            <Typography variant="h4">Build Intake</Typography>
          </div>
        </Fade>
        <Fade
          in={!showOptions && questions.length > 0}
          timeout={FADE_DURATION}
          unmountOnExit
        >
          <Stack gap={1}>
            <Typography variant="h5">Previous: {question?.title}</Typography>
            <Typography variant="h5">
              Suggested next question: {question?.next_question?.title}
            </Typography>
          </Stack>
        </Fade>
        <Fade in={showOptions} timeout={FADE_DURATION} unmountOnExit>
          <Stack>
            <Typography variant="h4">{question?.title}</Typography>
          </Stack>
        </Fade>
        <Fade
          in={showOptions && options.length > 0}
          timeout={FADE_DURATION}
          unmountOnExit
        >
          <List component="ol">
            {options.map((o) => (
              <ListItem key={o.position} sx={{ display: "list-item" }}>
                {`${o.position}) ${o.label}`}
              </ListItem>
            ))}
          </List>
        </Fade>
        <Fade in={!showOptions} timeout={FADE_DURATION} unmountOnExit>
          <div>
            <QuestionBuilder
              intakeId={Number(intakeId)}
              currentQuestion={question}
            />
          </div>
        </Fade>
        <Fade
          in={showOptions && options.length <= 0}
          timeout={FADE_DURATION}
          unmountOnExit
        >
          <div>
            <QuestionOptionsBuilder
              intakeId={Number(intakeId)}
              intakeQuestionId={question?.id}
              returnToQuestion={handleBackToBuilder}
            />
          </div>
        </Fade>
        {showOptions ? (
          <Button onClick={handleBackToBuilder} variant="secondary">
            Continue
          </Button>
        ) : null}
      </Stack>
    </Container>
  );
};

export default IntakeBuilder;
